import { useEffect, useState } from 'react';
import { Cpu, Coins, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import { fetchLlmUsage } from '../lib/api';
import { UsageDailyChart } from './nav/UsageDailyChart';

const nf = new Intl.NumberFormat('it-IT');
const fmtUsd = (v) => `$${Number(v || 0).toFixed(4)}`;
const fmtDateTime = (iso) => new Date(iso).toLocaleString('it-IT', {
  day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit',
});
const FEATURE_LABELS = {
  coach_gate: 'Filtro pertinenza',
  suggerisci_mit: 'Suggerisci MIT',
  recap_quote: 'Quote Year Recap',
};
const featureLabel = (f) => {
  if (FEATURE_LABELS[f]) return FEATURE_LABELS[f];
  const [kind, momento] = f.split(':');
  return `${kind === 'coach_stream' ? 'Coach (stream)' : 'Coach'}${momento ? ` · ${momento}` : ''}`;
};

/** PaginaConsumiAI — /consumi-ai: log chiamate Claude con token e costo stimato. */
export default function PaginaConsumiAI() {
  const [scope, setScope] = useState('me');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setData(null);
    setError(null);
    fetchLlmUsage({ scope, days })
      .then(setData)
      .catch((e) => setError(e?.response?.data?.detail || 'Errore caricamento consumi'));
  }, [scope, days]);

  return (
    <div className="max-w-[1200px] mx-auto px-6 py-10" data-testid="llm-usage-page">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl md:text-3xl tracking-tight">Consumi AI</h1>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-mute mt-1">
            Chiamate a Claude · token · costo stimato (listino Anthropic)
          </p>
        </div>
        <Filters scope={scope} setScope={setScope} days={days} setDays={setDays} isAdmin={data?.is_admin} />
      </header>

      {error && <div className="border border-risk/20 bg-risk/10 text-risk p-4 text-sm" data-testid="llm-usage-error">{error}</div>}
      {!error && !data && <div className="border border-linesoft bg-canvas h-40 animate-pulse" data-testid="llm-usage-loading" />}
      {data && <UsageContent data={data} />}
    </div>
  );
}

function Filters({ scope, setScope, days, setDays, isAdmin }) {
  const btn = (active) => `px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors ${active ? 'bg-ink text-paper border-ink' : 'border-line text-soft hover:text-ink'}`;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {isAdmin && (
        <div className="flex" data-testid="llm-usage-scope">
          <button type="button" className={btn(scope === 'me')} onClick={() => setScope('me')} data-testid="llm-usage-scope-me">Le mie</button>
          <button type="button" className={btn(scope === 'all')} onClick={() => setScope('all')} data-testid="llm-usage-scope-all">Tutti gli utenti</button>
        </div>
      )}
      <div className="flex" data-testid="llm-usage-days">
        {[7, 30, 90, 365].map((d) => (
          <button key={d} type="button" className={btn(days === d)} onClick={() => setDays(d)} data-testid={`llm-usage-days-${d}`}>{d}g</button>
        ))}
      </div>
    </div>
  );
}

function UsageContent({ data }) {
  const t = data.totals || { calls: 0, input_tokens: 0, output_tokens: 0, cache_tokens: 0, total_tokens: 0, cost_usd: 0 };
  return (
    <div className="space-y-10">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card icon={<Cpu size={16} strokeWidth={1.5} />} label="Chiamate" value={nf.format(t.calls)} testid="llm-total-calls" />
        <Card icon={<ArrowDownToLine size={16} strokeWidth={1.5} />} label="Token input" value={nf.format(t.input_tokens)} footer={`+ ${nf.format(t.cache_tokens)} cache`} testid="llm-total-input" />
        <Card icon={<ArrowUpFromLine size={16} strokeWidth={1.5} />} label="Token output" value={nf.format(t.output_tokens)} footer={`${nf.format(t.total_tokens)} totali`} testid="llm-total-output" />
        <Card icon={<Coins size={16} strokeWidth={1.5} />} label="Costo stimato" value={fmtUsd(t.cost_usd)} footer={`ultimi ${data.days} giorni`} testid="llm-total-cost" />
      </div>
      <UsageDailyChart byDay={data.by_day} days={data.days} />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AggTable title="Per giorno" rows={data.by_day} testid="llm-by-day" />
        <AggTable title="Per mese" rows={data.by_month} testid="llm-by-month" />
        <AggTable title="Per modello" rows={data.by_model} testid="llm-by-model" />
        <AggTable title="Per funzione" rows={data.by_feature} format={featureLabel} testid="llm-by-feature" />
        {data.scope === 'all' && <AggTable title="Per utente" rows={data.by_user} testid="llm-by-user" />}
      </div>
      <CallsTable calls={data.calls} showUser={data.scope === 'all'} />
    </div>
  );
}

function Card({ icon, label, value, footer, testid }) {
  return (
    <div className="border border-line bg-paper p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3 text-mute">
        {icon}
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-soft">{label}</span>
      </div>
      <div className="font-display text-3xl md:text-4xl tabular-nums tracking-tight" data-testid={`${testid}-value`}>{value}</div>
      {footer && <div className="mt-2 font-mono text-[11px] text-soft">{footer}</div>}
    </div>
  );
}

const TH = 'px-3 py-2 text-left font-mono text-[10px] uppercase tracking-[0.15em] text-mute font-normal';
const TD = 'px-3 py-2 tabular-nums';

function AggTable({ title, rows, format = (k) => k, testid }) {
  return (
    <section className="border border-line bg-paper" data-testid={testid}>
      <h2 className="px-4 py-3 border-b border-linesoft font-mono text-[11px] uppercase tracking-[0.2em] text-soft">{title}</h2>
      {rows.length === 0 ? <p className="px-4 py-6 text-sm text-mute">Nessuna chiamata nel periodo.</p> : (
        <div className="max-h-72 overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-linesoft"><th className={TH}>Voce</th><th className={TH}>Chiamate</th><th className={TH}>Token</th><th className={TH}>Costo</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-linesoft last:border-0">
                  <td className={`${TD} font-mono text-xs`}>{format(r.key)}</td>
                  <td className={TD}>{nf.format(r.calls)}</td>
                  <td className={TD}>{nf.format(r.total_tokens)}</td>
                  <td className={TD}>{fmtUsd(r.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CallsTable({ calls, showUser }) {
  return (
    <section className="border border-line bg-paper" data-testid="llm-calls-table">
      <h2 className="px-4 py-3 border-b border-linesoft font-mono text-[11px] uppercase tracking-[0.2em] text-soft">
        Ultime chiamate ({calls.length})
      </h2>
      {calls.length === 0 ? <p className="px-4 py-6 text-sm text-mute" data-testid="llm-calls-empty">Nessuna chiamata registrata nel periodo.</p> : (
        <div className="max-h-[480px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-paper"><tr className="border-b border-linesoft">
              <th className={TH}>Data e ora</th>{showUser && <th className={TH}>Utente</th>}<th className={TH}>Funzione</th><th className={TH}>Modello</th>
              <th className={TH}>Input</th><th className={TH}>Output</th><th className={TH}>Cache</th><th className={TH}>Totale</th><th className={TH}>Costo</th><th className={TH}>Durata</th>
            </tr></thead>
            <tbody>
              {calls.map((c) => (
                <tr key={c.id} className={`border-b border-linesoft last:border-0 ${c.ok ? '' : 'text-risk'}`} title={c.error || ''} data-testid={`llm-call-row-${c.id}`}>
                  <td className={`${TD} font-mono text-xs whitespace-nowrap`}>{fmtDateTime(c.created_at)}</td>
                  {showUser && <td className={`${TD} text-xs`}>{c.user_email || '—'}</td>}
                  <td className={`${TD} text-xs`}>{featureLabel(c.feature)}{!c.ok && ' · errore'}</td>
                  <td className={`${TD} font-mono text-xs`}>{c.model}</td>
                  <td className={TD}>{nf.format(c.input_tokens)}</td>
                  <td className={TD}>{nf.format(c.output_tokens)}</td>
                  <td className={TD}>{nf.format(c.cache_creation_tokens + c.cache_read_tokens)}</td>
                  <td className={TD}>{nf.format(c.total_tokens)}</td>
                  <td className={TD}>{fmtUsd(c.cost_usd)}</td>
                  <td className={`${TD} text-xs text-mute`}>{c.duration_ms != null ? `${(c.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
